import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Users, CheckCircle, ArrowRight, Phone, BarChart3, UserPlus,
  FileText, Shield, Zap, TrendingUp, Flame,
  Rocket, Trophy, Crown, MessageCircle, X, Check, Star, Award, Target
} from 'lucide-react';
import LocationSearch from '@/components/LocationSearch';
import TurnstileWidget from '@/components/common/TurnstileWidget';

// Icon mapping
const iconMap = {
  Flame, TrendingUp, Rocket, Trophy, Crown
};

// Color configurations
const colorConfig = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-500', badge: 'bg-emerald-500', gradient: 'from-emerald-500 to-teal-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-500', badge: 'bg-blue-500', gradient: 'from-blue-500 to-indigo-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', badge: 'bg-purple-500', gradient: 'from-purple-500 to-violet-500' },
  red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-500', badge: 'bg-red-500', gradient: 'from-red-500 to-rose-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-500', badge: 'bg-amber-500', gradient: 'from-amber-500 to-orange-500' },
};

const features = [
  { icon: BarChart3, title: 'Smart Analytics', description: 'Track views, inquiries, and conversions with AI-powered insights', color: 'bg-blue-500' },
  { icon: Target, title: 'Lead Management', description: 'Organize and prioritize leads with automated follow-ups', color: 'bg-green-500' },
  { icon: Award, title: 'Verified Badge', description: 'Build instant trust with ZenStudios verification seal', color: 'bg-amber-500' },
  { icon: MessageCircle, title: 'Instant Alerts', description: 'Real-time WhatsApp notifications for hot leads', color: 'bg-purple-500' },
];

// Feature comparison data for the chart
const comparisonFeatures = [
  { name: 'Rental Listings', ignite: '5', momentum: '10', accelerate: '20', dominate: '40', rule: 'Unlimited' },
  { name: 'Sale Listings', ignite: '0', momentum: '5', accelerate: '10', dominate: '20', rule: 'Unlimited' },
  { name: 'Listing Validity', ignite: '30 days', momentum: '45 days', accelerate: '60 days', dominate: '90 days', rule: 'Custom' },
  { name: 'Team Logins', ignite: '1', momentum: '1', accelerate: '2', dominate: '5', rule: 'Unlimited' },
  { name: 'Email Alerts', ignite: true, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'WhatsApp Alerts', ignite: false, momentum: false, accelerate: false, dominate: true, rule: true },
  { name: 'Priority Placement', ignite: false, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'Featured Listings', ignite: false, momentum: false, accelerate: '3 Active', dominate: '10 Active', rule: 'Unlimited' },
  { name: 'CRM Access', ignite: false, momentum: false, accelerate: 'Lite', dominate: 'Full', rule: 'Full' },
  { name: 'Analytics', ignite: 'Basic', momentum: 'Basic', accelerate: 'Advanced', dominate: 'Advanced', rule: 'Enterprise' },
  { name: 'Lead Routing', ignite: false, momentum: false, accelerate: true, dominate: true, rule: true },
  { name: 'API Access', ignite: false, momentum: false, accelerate: false, dominate: false, rule: true },
  { name: 'White Label', ignite: false, momentum: false, accelerate: false, dominate: false, rule: true },
  { name: 'Dedicated Manager', ignite: false, momentum: false, accelerate: false, dominate: false, rule: true },
];

const AgentPortal = () => {
  const navigate = useNavigate();
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [whatsappAddon, setWhatsappAddon] = useState({ price: 999, period: 'month' });
  const [activeBanner, setActiveBanner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    rera_number: '',
    city: '',
    state: '',
    experience: '',
    message: ''
  });

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const API_URL = '';
      const [tiersRes, waAddonRes, bannerRes] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/tiers/agent`),
        fetch(`${API_URL}/api/subscription-pricing/whatsapp-addon`),
        fetch(`${API_URL}/api/coupons/banners?business_unit=partner_subscription&user_type=agent`),
      ]);
      const data = await tiersRes.json();
      const waData = await waAddonRes.json();
      const bannerData = bannerRes.ok ? await bannerRes.json() : { banners: [] };
      setTiers(data.tiers || []);
      if (waData.addon) {
        setWhatsappAddon(waData.addon);
      }
      const promos = (bannerData.banners || []).filter(
        (b) => b.discount_type === 'percentage' && b.discount_value > 0
      );
      setActiveBanner(promos[0] || null);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setTiersLoading(false);
    }
  };

  // Discounted price when a percentage promo is active (respects tenure scoping)
  const getPromoPrice = (originalPrice) => {
    if (!activeBanner || originalPrice == null) return null;
    const tenure = activeBanner.subscription_tenure;
    const cycle = billingCycle === 'yearly' ? 'annual' : billingCycle;
    if (tenure && tenure !== cycle) return null;
    return Math.round(originalPrice * (1 - activeBanner.discount_value / 100));
  };

  const getPrice = (tier) => {
    if (tier.is_enterprise) return null;
    const pricing = tier.pricing || {};
    const key = billingCycle === 'yearly' ? 'annual' : billingCycle;
    return pricing[key] ?? pricing.monthly;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Custom';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getPeriodText = () => {
    if (billingCycle === 'monthly') return '/ month';
    if (billingCycle === 'six_month') return '/ 6 months';
    return '/ year';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.city || !formData.state) {
      toast.error('Please fill in all required fields including city and state');
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/leads/', {
        business_line: 'agent_registration',
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        message: formData.message,
        captcha_token: captchaToken,
        metadata: {
          company: formData.company,
          rera_number: formData.rera_number,
          state: formData.state,
          experience: formData.experience
        }
      });
      
      toast.success('Registration submitted! Our team will review and contact you within 24-48 hours.');
      setShowRegister(false);
      setCaptchaToken(null);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'CAPTCHA_VALIDATION_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
        setCaptchaToken(null);
      } else {
        toast.error('Failed to submit registration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFeatureValue = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-emerald-500 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-slate-300 mx-auto" />;
    return <span className="text-slate-700 font-medium">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="agent-portal-page">
      {/* Hero Section - Modern Redesign */}
      <section className="relative py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-emerald-50" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-5 py-2.5 rounded-full text-sm font-semibold mb-8 border border-primary/20">
                <Star className="w-4 h-4 fill-primary" /> For Independent Agents & Brokers
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="text-slate-900">Scale Your</span>
                <br />
                <span className="text-primary">Real Estate</span>
                <br />
                <span className="text-slate-900">Business</span>
              </h1>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-lg">
                Join India's fastest-growing property platform. Access premium tools, 
                verified leads, and features built specifically for ambitious brokers.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8 h-14 text-base shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                  onClick={() => setShowRegister(true)}
                  data-testid="agent-register-btn"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Get Started
                </Button>
                <Link to="/agent-login">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 h-14 text-base border-2 hover:bg-slate-50"
                    data-testid="agent-login-btn"
                  >
                    Agent Login
                  </Button>
                </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex items-center gap-8 mt-12 pt-8 border-t border-slate-200">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">500+</p>
                  <p className="text-sm text-slate-500">Active Agents</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">₹50Cr+</p>
                  <p className="text-sm text-slate-500">Monthly GMV</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">10K+</p>
                  <p className="text-sm text-slate-500">Leads Generated</p>
                </div>
              </div>
            </div>
            
            {/* Dashboard Preview - Enhanced */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-amber-400/20 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 overflow-hidden">
                <div className="absolute top-4 right-4 bg-amber-400 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-lg">
                  Live Dashboard
                </div>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 space-y-5">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 text-center group hover:shadow-md transition-all">
                      <p className="text-3xl font-bold text-primary">47</p>
                      <p className="text-xs text-slate-500 mt-1">Active Listings</p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 text-center group hover:shadow-md transition-all">
                      <p className="text-3xl font-bold text-emerald-600">156</p>
                      <p className="text-xs text-slate-500 mt-1">Leads This Month</p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 text-center group hover:shadow-md transition-all">
                      <p className="text-3xl font-bold text-amber-600">12</p>
                      <p className="text-xs text-slate-500 mt-1">Closures</p>
                    </div>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-slate-700">Performance Trend</span>
                      <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                        <TrendingUp className="w-3.5 h-3.5" />
                        +23%
                      </div>
                    </div>
                    <div className="h-24 bg-gradient-to-t from-primary/5 to-transparent rounded-lg flex items-end justify-around px-2 pb-2">
                      {[35, 55, 40, 70, 50, 85, 65].map((h, i) => (
                        <div 
                          key={i} 
                          className="w-6 bg-gradient-to-t from-primary to-primary/60 rounded-t-md transition-all hover:from-primary hover:to-primary/80" 
                          style={{height: `${h}%`}} 
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Modern Card Design */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Why ZenStudios</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Everything You Need to Close More Deals</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Powerful tools designed specifically for independent real estate professionals</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="bg-white p-8 rounded-2xl border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`feature-card-${idx}`}
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-3 text-slate-900">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers - Modern Design */}
      <section className="py-24 bg-white" data-testid="agent-pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Pricing Plans</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Choose Your Growth Plan</h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">Flexible packages designed for independent brokers at every stage</p>
            
            {/* Billing Cycle Toggle - Enhanced */}
            <div className="inline-flex bg-slate-100 p-1.5 rounded-2xl shadow-inner">
              {[
                { key: 'monthly', label: 'Monthly' },
                { key: 'six_month', label: '6 Months', savings: '10%' },
                { key: 'annual', label: 'Annual', savings: '15%' },
              ].map((cycle) => (
                <button
                  key={cycle.key}
                  onClick={() => setBillingCycle(cycle.key)}
                  data-testid={`billing-${cycle.key}`}
                  className={`px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                    billingCycle === cycle.key
                      ? 'bg-white text-slate-900 shadow-md'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {cycle.label}
                  {cycle.savings && billingCycle === cycle.key && (
                    <span className="ml-2 text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                      Save {cycle.savings}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {tiersLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {tiers.map((tier) => {
                const colors = colorConfig[tier.color] || colorConfig.emerald;
                const Icon = iconMap[tier.icon] || Flame;
                const price = getPrice(tier);
                const isPopular = tier.tier === 'accelerate';

                return (
                  <div
                    key={tier.tier}
                    data-testid={`tier-card-${tier.tier}`}
                    className={`bg-white rounded-3xl border-2 p-6 relative flex flex-col transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                      isPopular ? `${colors.border} shadow-xl ring-2 ring-primary/10` : 'border-slate-200 shadow-md'
                    }`}
                  >
                    {isPopular && (
                      <div className={`absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-gradient-to-r ${colors.gradient} text-white text-xs font-bold rounded-full shadow-lg`}>
                        ⭐ Most Popular
                      </div>
                    )}

                    {/* Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center shadow-sm`}>
                        <Icon className={`w-7 h-7 ${colors.text}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-slate-900">{tier.name}</h3>
                      </div>
                    </div>

                    {/* Tagline */}
                    <p className="text-sm text-slate-500 mb-5 leading-relaxed">{tier.tagline}</p>

                    {/* Price */}
                    <div className="mb-5 pb-5 border-b border-slate-100">
                      {tier.is_enterprise ? (
                        <div>
                          <span className="text-3xl font-bold text-slate-900">Custom</span>
                          <p className="text-sm text-slate-500 mt-1">Tailored for enterprise</p>
                        </div>
                      ) : (() => {
                        const promoPrice = getPromoPrice(price);
                        return (
                          <>
                            {promoPrice != null && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm line-through text-slate-400">{formatPrice(price)}</span>
                                <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                  {activeBanner.discount_value}% off
                                </span>
                              </div>
                            )}
                            <span className={`text-4xl font-bold ${promoPrice != null ? 'text-amber-600' : 'text-slate-900'}`}>
                              {formatPrice(promoPrice ?? price)}
                            </span>
                            <span className="text-slate-500 text-sm ml-1">{getPeriodText()}</span>
                            {promoPrice != null && (
                              <p className="text-xs text-amber-600 mt-1 font-medium" data-testid={`promo-savings-${tier.tier}`}>
                                Save {formatPrice(price - promoPrice)} with this promotion
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Key Highlights */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-5 text-sm space-y-2.5">
                      {tier.listing_limits && !tier.is_enterprise && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Rental Listings</span>
                            <span className="font-bold text-slate-900">{tier.listing_limits.rental}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Sale Listings</span>
                            <span className="font-bold text-slate-900">{tier.listing_limits.sale}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Validity</span>
                            <span className="font-bold text-slate-900">{tier.validity_days} days</span>
                          </div>
                        </>
                      )}
                      {tier.is_enterprise && (
                        <div className="text-center text-slate-600 py-2">Custom allocation based on needs</div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <div className="mt-auto">
                      {tier.is_enterprise ? (
                        <Button
                          className="w-full rounded-xl h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg"
                          onClick={() => window.open('mailto:enterprise@zenstudios.in?subject=Agent Enterprise Plan Inquiry', '_blank')}
                          data-testid={`enterprise-contact-${tier.tier}`}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Talk to Sales
                        </Button>
                      ) : (
                        <Button
                          className={`w-full rounded-xl h-12 font-semibold shadow-lg transition-all ${
                            isPopular
                              ? `bg-gradient-to-r ${colors.gradient} hover:opacity-90`
                              : 'bg-slate-900 hover:bg-slate-800'
                          }`}
                          onClick={() => setShowRegister(true)}
                          data-testid={`get-started-${tier.tier}`}
                        >
                          Get Started
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Feature Comparison Chart */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-4">Compare All Features</h2>
            <p className="text-gray-600">See what's included in each plan</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                    <th className="text-left py-4 px-6 font-semibold">Features</th>
                    <th className="text-center py-4 px-4 font-semibold">
                      <div className="flex flex-col items-center">
                        <Flame className="w-5 h-5 text-emerald-400 mb-1" />
                        <span>Ignite</span>
                      </div>
                    </th>
                    <th className="text-center py-4 px-4 font-semibold">
                      <div className="flex flex-col items-center">
                        <TrendingUp className="w-5 h-5 text-blue-400 mb-1" />
                        <span>Momentum</span>
                      </div>
                    </th>
                    <th className="text-center py-4 px-4 font-semibold bg-purple-900/50">
                      <div className="flex flex-col items-center">
                        <Rocket className="w-5 h-5 text-purple-400 mb-1" />
                        <span>Accelerate</span>
                        <span className="text-xs text-purple-300 mt-0.5">Popular</span>
                      </div>
                    </th>
                    <th className="text-center py-4 px-4 font-semibold">
                      <div className="flex flex-col items-center">
                        <Trophy className="w-5 h-5 text-red-400 mb-1" />
                        <span>Dominate</span>
                      </div>
                    </th>
                    <th className="text-center py-4 px-4 font-semibold">
                      <div className="flex flex-col items-center">
                        <Crown className="w-5 h-5 text-amber-400 mb-1" />
                        <span>Rule</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, idx) => (
                    <tr key={idx} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/50' : 'bg-white'}`}>
                      <td className="py-3 px-6 font-medium text-slate-700">{feature.name}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.ignite)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.momentum)}</td>
                      <td className="py-3 px-4 text-center bg-purple-50/50">{renderFeatureValue(feature.accelerate)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.dominate)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.rule)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Add-on Section - Modern Design */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl p-8 md:p-10 shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative flex flex-col md:flex-row items-start gap-8">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-xl">
                <MessageCircle className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-grow text-white">
                <h3 className="font-bold text-2xl mb-2">{whatsappAddon.name || 'WhatsApp Lead Alerts Add-on'}</h3>
                <p className="text-white/80 mb-5">Get instant lead notifications directly on WhatsApp - never miss a hot lead again</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(whatsappAddon.features || ['Instant Notifications', 'Auto Lead Summary', 'Click-to-Respond', 'Missed Lead Alerts']).map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-white/90">
                      <Check className="w-4 h-4 text-white" />
                      {feature}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/60 mt-4">
                  ✓ Included FREE with Dominate & Rule packages • Available as add-on for other plans
                </p>
              </div>
              <div className="text-center flex-shrink-0 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-white">₹{whatsappAddon.price?.toLocaleString('en-IN') || '999'}</div>
                <div className="text-sm text-white/70 mb-4">/ {whatsappAddon.period || 'month'}</div>
                <Button 
                  className="bg-white text-green-600 hover:bg-white/90 rounded-xl font-semibold shadow-lg" 
                  onClick={() => setShowRegister(true)}
                  data-testid="whatsapp-addon-btn"
                >
                  Add to Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Modern Design */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary to-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-5">Ready to Supercharge Your Business?</h2>
          <p className="text-white/80 mb-10 text-lg max-w-2xl mx-auto">Join 500+ successful agents already growing with ZenStudios. Get started today.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/95 px-10 h-14 text-base font-semibold shadow-xl"
              onClick={() => setShowRegister(true)}
              data-testid="cta-register-btn"
            >
              Get Started
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full border-2 border-white/40 text-white hover:bg-white/10 px-10 h-14 text-base font-semibold"
              onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20a%20real%20estate%20agent%20interested%20in%20your%20partner%20program.', '_blank')}
              data-testid="cta-talk-sales-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Registration Modal - Enhanced */}
      {showRegister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Join as Agent</h2>
                <p className="text-sm text-slate-500 mt-1">Tell us about your business — our team will reach out within 24–48 hours</p>
              </div>
              <button 
                onClick={() => setShowRegister(false)}
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="rounded-xl mt-1.5 h-11 border-slate-200 focus:border-primary focus:ring-primary/20"
                    placeholder="John Doe"
                    data-testid="agent-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="rounded-xl mt-1.5 h-11 border-slate-200 focus:border-primary focus:ring-primary/20"
                    placeholder="+91 98765 43210"
                    data-testid="agent-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="rounded-xl mt-1.5 h-11 border-slate-200 focus:border-primary focus:ring-primary/20"
                  placeholder="john@example.com"
                  data-testid="agent-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Company/Agency</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="rounded-xl mt-1.5 h-11 border-slate-200 focus:border-primary focus:ring-primary/20"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">RERA Number</Label>
                  <Input
                    value={formData.rera_number}
                    onChange={(e) => setFormData({...formData, rera_number: e.target.value})}
                    placeholder="Optional"
                    className="rounded-xl mt-1.5 h-11 border-slate-200 focus:border-primary focus:ring-primary/20"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">City *</Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => setFormData({...formData, city: val})}
                    placeholder="Search city..."
                    types="city,town"
                    required
                    className="rounded-xl mt-1.5"
                    data-testid="agent-city"
                    onLocationSelect={(location) => {
                      if (location?.state) setFormData(prev => ({...prev, state: location.state}));
                    }}
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">State *</Label>
                  <LocationSearch
                    value={formData.state}
                    onChange={(val) => setFormData({...formData, state: val})}
                    placeholder="Search state..."
                    types="state"
                    required
                    className="rounded-xl mt-1.5"
                    data-testid="agent-state"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Years of Experience</Label>
                <Select value={formData.experience} onValueChange={(v) => setFormData({...formData, experience: v})}>
                  <SelectTrigger className="rounded-xl mt-1.5 h-11 border-slate-200">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-1">0-1 Years</SelectItem>
                    <SelectItem value="1-3">1-3 Years</SelectItem>
                    <SelectItem value="3-5">3-5 Years</SelectItem>
                    <SelectItem value="5-10">5-10 Years</SelectItem>
                    <SelectItem value="10+">10+ Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Tell us about your business</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={3}
                  className="rounded-xl mt-1.5 border-slate-200 focus:border-primary focus:ring-primary/20"
                  placeholder="Brief description of your experience and goals..."
                />
              </div>
              <TurnstileWidget
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
                className="flex justify-center"
              />
              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowRegister(false); setCaptchaToken(null); }} 
                  className="flex-1 rounded-full h-12 border-2"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 rounded-full h-12 bg-primary hover:bg-primary/90 font-semibold shadow-lg" 
                  disabled={loading || !captchaToken}
                  data-testid="submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPortal;
